import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import type { Config, Driver } from 'driver.js';
import { useAuthContext } from '../contexts/AuthContext';
import { isOrgManager } from '../lib/orgRoles';
import { useTeamDashboardWalkthroughContext } from '../contexts/TeamDashboardWalkthroughContext';
import api from '../lib/api';
import { CURRENT_TEAM_DASHBOARD_TOUR_VERSION } from '../constants/teamDashboardTour';
import { buildTeamDashboardWalkthroughSteps } from '../teamWalkthrough/buildTeamDashboardWalkthroughSteps';
import { STORAGE_PENDING_TEAM_MANUAL } from '../teamWalkthrough/storageKeys';

async function postTeamDashboardTourComplete(): Promise<void> {
  try {
    await api.post('/auth/me/team-dashboard-tour/complete');
  } catch {
    // non-blocking
  }
}

function isTeamDashboardReady(): boolean {
  return Boolean(
    document.querySelector('[data-tour="team-kpis"]') ||
    document.querySelector('[data-tour="team-error"]'),
  );
}

type ReadyWaitSignal = { cancelled: boolean; timeoutId?: number };

function waitForTeamDashboardReady(signal: ReadyWaitSignal, timeoutMs = 2000): Promise<void> {
  return new Promise((resolve) => {
    const started = Date.now();
    const check = () => {
      if (signal.cancelled) {
        resolve();
        return;
      }
      if (isTeamDashboardReady() || Date.now() - started >= timeoutMs) {
        resolve();
        return;
      }
      window.setTimeout(check, 100);
    };
    check();
  });
}

export function TeamDashboardWalkthrough() {
  const { user } = useAuthContext();
  const { manualTeamDashboardRequest } = useTeamDashboardWalkthroughContext();
  const location = useLocation();
  const pathname = location.pathname;

  const driverRef = useRef<Driver | null>(null);
  const autoStartedRef = useRef(false);
  const scheduleGuardRef = useRef(false);
  const lastManualRequestRef = useRef(0);

  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [questionnaireCompleted, setQuestionnaireCompleted] = useState<boolean | null>(null);
  const [tourVersion, setTourVersion] = useState<number | null>(null);

  const runTourRef = useRef<(isAuto: boolean) => void>(() => {});

  const destroyDriver = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api
        .get<{ teamDashboardTourVersionCompleted?: number }>('/auth/me')
        .then((res) => res.data.teamDashboardTourVersionCompleted ?? 0)
        .catch(() => CURRENT_TEAM_DASHBOARD_TOUR_VERSION),
      api
        .get<{ questionnaireCompleted?: boolean }>('/orgs/me')
        .then((res) => res.data.questionnaireCompleted ?? true)
        .catch(() => true),
    ]).then(([version, qc]) => {
      if (cancelled) return;
      setTourVersion(version);
      setQuestionnaireCompleted(qc);
      setPrefsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const runTour = useCallback(
    (isAuto: boolean) => {
      if (!user) return;
      if (!isOrgManager(user.orgRole)) return;
      if (isAuto && questionnaireCompleted === false) return;
      if (pathname !== '/team') return;

      if (!isAuto) {
        try {
          sessionStorage.removeItem(STORAGE_PENDING_TEAM_MANUAL);
        } catch {
          // ignore
        }
      }

      const steps = buildTeamDashboardWalkthroughSteps();

      const onDestroyed: NonNullable<Config['onDestroyed']> = () => {
        void postTeamDashboardTourComplete();
        driverRef.current = null;
        setTourVersion(CURRENT_TEAM_DASHBOARD_TOUR_VERSION);
      };

      destroyDriver();

      const d = driver({
        showProgress: true,
        smoothScroll: true,
        allowClose: true,
        overlayOpacity: 0.55,
        stagePadding: 8,
        popoverClass: 'akoreps-driver-popover',
        steps,
        onDestroyed,
      });

      driverRef.current = d;
      d.drive();
    },
    [user, questionnaireCompleted, pathname, destroyDriver],
  );

  runTourRef.current = runTour;

  useEffect(() => {
    if (pathname !== '/team') {
      autoStartedRef.current = false;
      scheduleGuardRef.current = false;
      return;
    }
    if (!prefsLoaded) return;
    if (!user || !isOrgManager(user.orgRole)) return;

    let manualIntent = false;
    try {
      manualIntent = sessionStorage.getItem(STORAGE_PENDING_TEAM_MANUAL) === '1';
    } catch {
      manualIntent = false;
    }

    const wantAuto =
      !manualIntent &&
      questionnaireCompleted === true &&
      tourVersion !== null &&
      tourVersion < CURRENT_TEAM_DASHBOARD_TOUR_VERSION;

    if (!manualIntent && !wantAuto) return;
    if (!manualIntent && wantAuto && autoStartedRef.current) return;
    if (!manualIntent && wantAuto) autoStartedRef.current = true;

    if (scheduleGuardRef.current) return;
    scheduleGuardRef.current = true;

    const signal: ReadyWaitSignal = { cancelled: false };
    const isAuto = !manualIntent && wantAuto;
    void waitForTeamDashboardReady(signal).then(() => {
      if (signal.cancelled) return;
      signal.timeoutId = window.setTimeout(() => runTourRef.current(isAuto), 400);
    });

    return () => {
      signal.cancelled = true;
      if (signal.timeoutId != null) window.clearTimeout(signal.timeoutId);
      scheduleGuardRef.current = false;
    };
  }, [pathname, prefsLoaded, questionnaireCompleted, tourVersion, user]);

  useEffect(() => {
    if (manualTeamDashboardRequest < 1) return;
    if (manualTeamDashboardRequest === lastManualRequestRef.current) return;
    lastManualRequestRef.current = manualTeamDashboardRequest;
    if (pathname !== '/team') return;
    const signal: ReadyWaitSignal = { cancelled: false };
    void waitForTeamDashboardReady(signal).then(() => {
      if (signal.cancelled) return;
      signal.timeoutId = window.setTimeout(() => runTourRef.current(false), 0);
    });
    return () => {
      signal.cancelled = true;
      if (signal.timeoutId != null) window.clearTimeout(signal.timeoutId);
    };
  }, [manualTeamDashboardRequest, pathname]);

  useEffect(
    () => () => {
      destroyDriver();
    },
    [destroyDriver],
  );

  return null;
}
